import React from 'react';
import {
  FixedPrice,
  TimeManage,
  OntimeDelivery,
} from "../assets/data/Imagedata";
import { FaShieldAlt, FaBalanceScale, FaGavel, FaLightbulb, FaHandsHelping, FaUserShield } from "react-icons/fa";

const engagementModels = [
  {
    icon: FixedPrice,
    iconComponent: <FaShieldAlt className="text-4xl" />,
    title: "Awareness, Education & Protection",
    description: "Empowering consumers with awareness, education & protection.",
    badge: "Primary Initiative",
    color: "from-blue-600 to-indigo-600",
    bgColor: "bg-gradient-to-br from-blue-50 to-indigo-50",
    borderColor: "border-blue-200",
    index: 1
  },
  {
    icon: TimeManage,
    iconComponent: <FaBalanceScale className="text-4xl" />,
    title: "Consumer Grievance Resolution",
    description: "We help consumers resolve grievances by providing guidance, support, and advocacy to ensure fair resolutions.",
    badge: "Core Service",
    color: "from-orange-600 to-amber-600",
    bgColor: "bg-gradient-to-br from-orange-50 to-amber-50",
    borderColor: "border-orange-200",
    index: 2
  },
  {
    icon: OntimeDelivery,
    iconComponent: <FaGavel className="text-4xl" />,
    title: "Free Legal Assistance",
    description: "We offer free legal aid to support consumers, resolve disputes, and uphold their rights for a fair marketplace.",
    badge: "Pro Bono Support",
    color: "from-green-600 to-emerald-600",
    bgColor: "bg-gradient-to-br from-green-50 to-emerald-50",
    borderColor: "border-green-200",
    index: 3
  },
];

export default function LineofEngagement() {
  return (
    <section className="cursor-default py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-10 w-64 h-64 bg-blue-100 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-orange-100 rounded-full opacity-10 blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full"></div>
            <span className="text-sm font-semibold tracking-wider text-blue-600 uppercase">
              Our Core Services
            </span>
            <div className="w-12 h-1 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full"></div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Areas of Engagement</span>
          </h2>
          
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Dedicated to protecting consumer rights, ensuring fair trade practices, 
            and empowering consumers across India through comprehensive support systems.
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {engagementModels.map((model) => (
            <div
              key={model.title}
              className="group relative"
            >
              {/* Decorative number background */}
              <div className="absolute -top-4 -left-4 w-16 h-16 flex items-center justify-center opacity-10 group-hover:opacity-20 transition-opacity duration-300">
                <span className="text-6xl font-black text-gray-400">{model.index}</span>
              </div>

              {/* Main Card */}
              <div className={`relative bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border ${model.borderColor} group-hover:-translate-y-2 h-full`}>
                {/* Corner accents */}
                <div className={`absolute top-0 right-0 w-16 h-16 overflow-hidden rounded-tr-2xl`}>
                  <div className={`absolute top-0 right-0 w-8 h-8 bg-gradient-to-br ${model.color} opacity-10 rounded-bl-full`}></div>
                </div>
                
                {/* Badge */}
                <div className="mb-6">
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${model.bgColor} ${model.borderColor} border text-sm font-semibold`}>
                    <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${model.color}`}></span>
                    {model.badge}
                  </span>
                </div>

                {/* Icon Container */}
                <div className={`relative w-20 h-20 rounded-2xl ${model.bgColor} flex items-center justify-center mb-6 mx-auto border ${model.borderColor} group-hover:scale-110 transition-transform duration-500`}>
                  {/* Animated background effect */}
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${model.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                  
                  {/* Image Icon */}
                  <img
                    src={model.icon}
                    alt={model.title}
                    className="w-12 h-12 relative z-10"
                    loading="lazy"
                  />
                </div>

                {/* Content */}
                <div className="text-center space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-gray-800 transition-colors duration-300">
                    {model.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {model.description}
                  </p>
                </div>

              

                {/* Hover effect line */}
                <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-1 bg-gradient-to-r ${model.color} group-hover:w-full transition-all duration-500 rounded-full`}></div>
              </div>

              {/* Floating element on hover */}
              <div className={`absolute -bottom-4 -right-4 w-8 h-8 rounded-full bg-gradient-to-r ${model.color} opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-md`}></div>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 text-center border border-blue-100">
            <div className="text-4xl font-bold text-blue-600 mb-2">25K+</div>
            <div className="text-gray-700 font-medium">Consumers Empowered</div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-6 text-center border border-orange-100">
            <div className="text-4xl font-bold text-orange-600 mb-2">95%</div>
            <div className="text-gray-700 font-medium">Grievance Resolution Rate</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-6 text-center border border-green-100">
            <div className="text-4xl font-bold text-green-600 mb-2">12K+</div>
            <div className="text-gray-700 font-medium">Free Legal Consultations</div>
          </div>
        </div>

     
      </div>
    </section>
  );
}